import { addToCart, formatPrice } from '@nova/utils'

function getConfig(container) {
  const script = container.querySelector('script[data-orp-ctb-config]')
  if (!script || !script.textContent) return null
  try {
    return JSON.parse(script.textContent.trim())
  } catch {
    return null
  }
}

function getMainFormVariantId() {
  const input = document.querySelector('.js-product-form input[name="id"]')
  return input ? Number(input.value) || null : null
}

function cardHasVariants(card) {
  return Boolean(card.querySelector('[data-orp-ctb-select]'))
}

function getSelections(card) {
  try {
    const raw = card.dataset.orpCtbSelections
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
  } catch {
    return {}
  }
}

function setSelections(card, map) {
  card.dataset.orpCtbSelections = JSON.stringify(map || {})
}

function getSelectionLines(card) {
  return Object.entries(getSelections(card))
    .map(([variantId, line]) => ({ variantId, ...line }))
    .filter((line) => (parseInt(line.qty, 10) || 0) > 0)
}

function getSelectionsTotalQty(card) {
  return getSelectionLines(card).reduce((sum, line) => sum + (parseInt(line.qty, 10) || 0), 0)
}

function getInputQty(card) {
  const inp = card.querySelector('[data-orp-ctb-qty-input]')
  if (!inp) return 0
  const n = parseInt(inp.value, 10)
  return Number.isFinite(n) && n >= 0 ? Math.min(99, n) : 0
}

function setInputQty(card, qty) {
  const inp = card.querySelector('[data-orp-ctb-qty-input]')
  if (!inp) return
  inp.value = String(Math.max(0, Math.min(99, qty)))
}

function getCardQty(card) {
  if (cardHasVariants(card)) return getSelectionsTotalQty(card)
  return getInputQty(card)
}

function cardIncluded(card) {
  return getCardQty(card) > 0
}

function setCardIncludedState(card, included) {
  card.dataset.orpCtbIncluded = included ? 'true' : 'false'
  card.classList.toggle('orp-ctb__card--included', included)
}

function syncQtyState(card) {
  const dec = card.querySelector('[data-orp-ctb-qty-dec]')
  const qty = getCardQty(card)
  if (dec) dec.disabled = qty <= 0
  setCardIncludedState(card, qty > 0)
  if (cardHasVariants(card)) setInputQty(card, qty)
}

function getVariantPrice(config, productId, variantId) {
  const product = config.products.find((p) => String(p.productId) === String(productId))
  const v = product?.variants?.find((v) => String(v.id) === String(variantId))
  return v ? v.price : 0
}

function getMainProductVariantPrice(config, variantId) {
  if (!config.mainProductVariants || !variantId) return 0
  const v = config.mainProductVariants.find((v) => String(v.id) === String(variantId))
  return v ? v.price : 0
}

function findOptionByVariantId(select, variantId) {
  if (!select) return null
  return Array.from(select.options).find((opt) => String(opt.value) === String(variantId)) || null
}

function lineFromOption(option, qty) {
  return {
    qty: Math.max(1, Math.min(99, qty)),
    title: (option.textContent || '').replace(/\s*—\s*Sold out\s*$/i, '').trim(),
    price: parseInt(option.dataset.price, 10) || 0,
    imageUrl: option.dataset.imageUrl || '',
    available: option.dataset.available !== 'false',
  }
}

function upsertSelectionWithSym(card, variantId, qty, sym, { replaceQty = false } = {}) {
  const select = card.querySelector('[data-orp-ctb-select]')
  const option = findOptionByVariantId(select, variantId)
  if (!option || option.dataset.available === 'false') return false

  const map = getSelections(card)
  const key = String(variantId)
  const existing = map[key]
  const nextQty = replaceQty
    ? Math.max(0, Math.min(99, qty))
    : Math.min(99, (existing?.qty || 0) + Math.max(1, Math.min(99, qty)))

  if (nextQty <= 0) {
    delete map[key]
  } else {
    map[key] = { ...lineFromOption(option, nextQty), qty: nextQty }
  }

  setSelections(card, map)

  if (nextQty > 0) {
    select.value = key
    updateCardFromSelect(card, option, sym)
  } else {
    const lines = getSelectionLines(card)
    if (lines.length) {
      const last = lines[lines.length - 1]
      const lastOpt = findOptionByVariantId(select, last.variantId)
      if (lastOpt) {
        select.value = String(last.variantId)
        updateCardFromSelect(card, lastOpt, sym)
      }
    }
  }

  syncQtyState(card)
  return true
}

function clearSelections(card, sym) {
  setSelections(card, {})
  setInputQty(card, 0)
  syncQtyState(card)
  if (sym) {
    const select = card.querySelector('[data-orp-ctb-select]')
    const option = select?.options[select.selectedIndex]
    if (option) updateCardFromSelect(card, option, sym)
  }
}

function seedSelectionsFromCard(card, sym) {
  if (!cardHasVariants(card)) return
  if (getSelectionLines(card).length) {
    syncQtyState(card)
    return
  }
  const qty = getInputQty(card)
  if (qty <= 0) {
    setSelections(card, {})
    syncQtyState(card)
    return
  }
  const select = card.querySelector('[data-orp-ctb-select]')
  const option = select?.options[select.selectedIndex]
  if (!option || option.dataset.available === 'false') return
  setSelections(card, {
    [String(option.value)]: lineFromOption(option, qty),
  })
  updateCardFromSelect(card, option, sym)
  syncQtyState(card)
}

function adjustSingleLineQty(card, nextQty, sym) {
  const lines = getSelectionLines(card)
  if (lines.length !== 1) return false
  const line = lines[0]
  upsertSelectionWithSym(card, line.variantId, nextQty, sym, { replaceQty: true })
  return true
}

function computeBundleTotal(container, config) {
  const mainVariantId = getMainFormVariantId()
  let total = config.showCurrentProduct ? 0 : getMainProductVariantPrice(config, mainVariantId)

  container.querySelectorAll('.orp-ctb__card').forEach((card) => {
    if (!cardIncluded(card)) return
    const productId = card.dataset.orpCtbProductId

    if (cardHasVariants(card)) {
      getSelectionLines(card).forEach((line) => {
        const price = line.price || getVariantPrice(config, productId, line.variantId)
        total += price * (parseInt(line.qty, 10) || 0)
      })
      return
    }

    const variantId = card.dataset.orpCtbVariantId
    if (variantId && productId) {
      total += getVariantPrice(config, productId, variantId) * getCardQty(card)
    }
  })

  return total
}

function formatBundleTotal(cents, currencySymbol) {
  const major = (cents || 0) / 100
  return `${currencySymbol}${major.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function updateBundleTotal(container, config) {
  const el = container.querySelector('[data-orp-ctb-bundle-total]')
  if (!el) return
  el.textContent = formatBundleTotal(computeBundleTotal(container, config), config.currencySymbol || '$')
}

function collectFooterThumbEntries(container) {
  const entries = []

  container.querySelectorAll('.orp-ctb__card').forEach((card) => {
    if (!cardIncluded(card)) return

    const productTitle = card.querySelector('.orp-ctb__card-name')?.textContent.trim() || 'item'

    if (cardHasVariants(card)) {
      getSelectionLines(card).forEach((line) => {
        entries.push({
          card,
          variantId: String(line.variantId),
          qty: parseInt(line.qty, 10) || 1,
          imageUrl: line.imageUrl || card.querySelector('[data-orp-ctb-image]')?.src || '',
          label: line.title ? `${productTitle}: ${line.title}` : productTitle,
        })
      })
      return
    }

    entries.push({
      card,
      variantId: null,
      qty: getCardQty(card) || 1,
      imageUrl: card.querySelector('[data-orp-ctb-image]')?.src || '',
      label: productTitle,
    })
  })

  return entries
}

function updateFooterThumbs(container, config) {
  const thumbsEl = container.querySelector('[data-orp-ctb-thumbs]')
  const eqEl = container.querySelector('[data-orp-ctb-eq]')
  if (!thumbsEl || !eqEl) return

  const entries = collectFooterThumbEntries(container)
  const sym = config.currencySymbol || '$'

  thumbsEl.innerHTML = ''

  if (!entries.length) {
    thumbsEl.hidden = true
    eqEl.hidden = true
    return
  }

  entries.forEach((entry, index) => {
    if (index > 0) thumbsEl.appendChild(createThumbPlus())
    thumbsEl.appendChild(createThumbItem(entry, container, config, sym))
  })

  thumbsEl.hidden = false
  eqEl.hidden = false
}

function createThumbItem(entry, container, config, sym) {
  const { card, variantId, qty, imageUrl, label } = entry

  const item = document.createElement('div')
  item.className = 'orp-ctb__footer-thumb-item'

  const qtyBadge = document.createElement('span')
  qtyBadge.className = 'orp-ctb__footer-thumb-qty'
  qtyBadge.textContent = `${qty}×`
  qtyBadge.setAttribute('aria-label', `Quantity ${qty}`)
  item.appendChild(qtyBadge)

  const thumb = document.createElement('img')
  thumb.className = 'orp-ctb__footer-thumb'
  thumb.src = imageUrl || ''
  thumb.alt = ''
  item.appendChild(thumb)

  const removeBtn = document.createElement('button')
  removeBtn.type = 'button'
  removeBtn.className = 'orp-ctb__footer-thumb-remove'
  removeBtn.setAttribute('aria-label', `Remove ${label} from bundle`)
  removeBtn.innerHTML = '&times;'
  removeBtn.addEventListener('click', () => {
    if (cardHasVariants(card) && variantId) {
      upsertSelectionWithSym(card, variantId, 0, sym, { replaceQty: true })
    } else if (cardHasVariants(card)) {
      clearSelections(card, sym)
    } else {
      setInputQty(card, 0)
      syncQtyState(card)
    }
    refresh(container, config)
  })
  item.appendChild(removeBtn)

  return item
}

function createThumbPlus() {
  const plus = document.createElement('span')
  plus.className = 'orp-ctb__footer-thumb-plus'
  plus.textContent = '+'
  return plus
}

function isAnyVariantUnavailable(container, config) {
  const mainVariantId = getMainFormVariantId()

  if (!config.showCurrentProduct && mainVariantId && config.mainProductVariants) {
    const main = config.mainProductVariants.find((v) => String(v.id) === String(mainVariantId))
    if (main && !main.available) return true
  }

  for (const card of container.querySelectorAll('.orp-ctb__card')) {
    if (!cardIncluded(card)) continue

    if (cardHasVariants(card)) {
      const select = card.querySelector('[data-orp-ctb-select]')
      for (const line of getSelectionLines(card)) {
        const option = findOptionByVariantId(select, line.variantId)
        if (option?.dataset.available === 'false' || line.available === false) return true
      }
      continue
    }

    const select = card.querySelector('[data-orp-ctb-select]')
    if (!select) continue
    const option = select.options[select.selectedIndex]
    if (option?.dataset.available === 'false') return true
  }
  return false
}

function buildAddAllItems(container, config) {
  const mainVariantId = getMainFormVariantId()
  const items = []

  if (!config.showCurrentProduct && mainVariantId) {
    items.push({ id: String(mainVariantId), quantity: 1 })
  }

  container.querySelectorAll('.orp-ctb__card').forEach((card) => {
    if (!cardIncluded(card)) return

    if (cardHasVariants(card)) {
      getSelectionLines(card).forEach((line) => {
        items.push({ id: String(line.variantId), quantity: parseInt(line.qty, 10) || 0 })
      })
      return
    }

    const variantId = card.dataset.orpCtbVariantId
    if (variantId) items.push({ id: String(variantId), quantity: getCardQty(card) })
  })

  return items.filter((item) => item.quantity > 0)
}

function updateAddAllButton(container, config) {
  const btn = container.querySelector('[data-orp-ctb-add-all]')
  if (!btn) return
  btn.disabled = buildAddAllItems(container, config).length === 0 || isAnyVariantUnavailable(container, config)
}

function setPriceCompare(currentEl, compareEl, price, compareAtPrice, currencySymbol) {
  const priceCents = parseInt(price, 10) || 0
  const compareCents = parseInt(compareAtPrice, 10) || 0
  if (currentEl) currentEl.textContent = formatPrice(priceCents, currencySymbol)
  if (!compareEl) return
  if (compareCents > priceCents) {
    compareEl.textContent = formatPrice(compareCents, currencySymbol)
    compareEl.hidden = false
  } else {
    compareEl.textContent = ''
    compareEl.hidden = true
  }
}

function updateCardFromSelect(card, option, currencySymbol) {
  if (option.dataset.imageUrl) {
    const img = card.querySelector('[data-orp-ctb-image]')
    if (img) img.src = option.dataset.imageUrl
  }
  if (option.dataset.price) {
    setPriceCompare(
      card.querySelector('[data-orp-ctb-current-price]'),
      card.querySelector('[data-orp-ctb-compare-price]'),
      option.dataset.price,
      option.dataset.compareAtPrice,
      currencySymbol,
    )
  }
  card.dataset.orpCtbVariantId = option.value
  card.dataset.orpCtbPrice = option.dataset.price
}

function syncCurrentProductCard(container, config, currencySymbol) {
  if (!config.showCurrentProduct) return
  const mainVariantId = getMainFormVariantId()
  if (!mainVariantId) return

  const currentCards = container.querySelectorAll('[data-orp-ctb-current="true"]')
  if (!currentCards.length) return

  const productData = config.products.find(
    (p) => String(p.productId) === String(currentCards[0].dataset.orpCtbProductId),
  )
  if (!productData?.variants) return

  const variant = productData.variants.find((v) => String(v.id) === String(mainVariantId))
  if (!variant) return

  currentCards.forEach((card) => {
    const select = card.querySelector('[data-orp-ctb-select]')
    if (select) select.value = String(variant.id)

    card.dataset.orpCtbVariantId = String(variant.id)
    card.dataset.orpCtbPrice = String(variant.price)

    setPriceCompare(
      card.querySelector('[data-orp-ctb-current-price]'),
      card.querySelector('[data-orp-ctb-compare-price]'),
      variant.price,
      variant.compareAtPrice,
      currencySymbol,
    )

    if (variant.featuredImageUrl) {
      const img = card.querySelector('[data-orp-ctb-image]')
      if (img) img.src = variant.featuredImageUrl
    }

    if (!cardHasVariants(card)) return

    const lines = getSelectionLines(card)
    const option = findOptionByVariantId(select, variant.id)
    if (!option) return

    if (lines.length === 0 && getInputQty(card) > 0) {
      setSelections(card, {
        [String(variant.id)]: lineFromOption(option, getInputQty(card)),
      })
    } else if (lines.length === 1) {
      const qty = lines[0].qty
      setSelections(card, {
        [String(variant.id)]: lineFromOption(option, qty),
      })
    }

    syncQtyState(card)
  })
}

function initCarousel(container) {
  const swiperEl = container.querySelector('[data-orp-ctb-swiper]')
  if (!swiperEl) return

  let attempts = 0
  const tryInit = () => {
    if (++attempts > 100) return
    const Swiper = typeof window !== 'undefined' ? window.Swiper : undefined
    if (!Swiper) { window.setTimeout(tryInit, 48); return }

    const root = container.querySelector('[data-orp-ctb-carousel-root]')
    const prevEl = root?.querySelector('.orp-ctb__carousel-nav--prev') ?? null
    const nextEl = root?.querySelector('.orp-ctb__carousel-nav--next') ?? null

    const swiper = new Swiper(swiperEl, {
      slidesPerView: 'auto',
      spaceBetween: 14,
      freeMode: false,
      grabCursor: true,
      watchOverflow: true,
      centerInsufficientSlides: true,
      touchMoveStopPropagation: true,
      keyboard: { enabled: false },
      mousewheel: false,
      navigation: prevEl && nextEl ? { nextEl, prevEl } : undefined,
    })
    swiperEl._orpCtbSwiper = swiper

    window.requestAnimationFrame(() => {
      try { swiper.update() } catch { /* ignore */ }
    })
  }
  tryInit()
}

function destroyModalThumbsSwiper(overlay) {
  const swiper = overlay?._orpCtbThumbsSwiper
  if (!swiper) return
  try {
    swiper.destroy(true, true)
  } catch {
    /* ignore */
  }
  overlay._orpCtbThumbsSwiper = null
}

function setModalFeaturedImage(modalImage, thumbsRoot, src, alt, { slideToActive = true } = {}) {
  if (!modalImage || !src) return
  modalImage.src = src
  if (alt != null) modalImage.alt = alt

  if (!thumbsRoot) return
  let activeIndex = -1
  thumbsRoot.querySelectorAll('[data-orp-ctb-modal-thumb]').forEach((btn, index) => {
    const active = btn.dataset.featuredUrl === src || btn.dataset.thumbUrl === src
    btn.classList.toggle('is-active', active)
    btn.setAttribute('aria-pressed', active ? 'true' : 'false')
    if (active) activeIndex = index
  })

  const overlay = thumbsRoot.closest('[data-orp-ctb-variant-modal-overlay]')
  const swiper = overlay?._orpCtbThumbsSwiper
  if (slideToActive && swiper && activeIndex >= 0) {
    try {
      swiper.slideTo(activeIndex)
    } catch {
      /* ignore */
    }
  }
}

function initModalThumbsSwiper(overlay, thumbsEl, prevEl, nextEl) {
  destroyModalThumbsSwiper(overlay)

  const Swiper = typeof window !== 'undefined' ? window.Swiper : undefined
  if (!Swiper || !thumbsEl) return null

  const swiper = new Swiper(thumbsEl, {
    slidesPerView: 'auto',
    spaceBetween: 10,
    freeMode: true,
    watchOverflow: true,
    grabCursor: true,
    navigation: prevEl && nextEl ? { prevEl, nextEl } : undefined,
  })
  overlay._orpCtbThumbsSwiper = swiper
  return swiper
}

function renderModalGallery(overlay, productData, fallbackSrc, alt) {
  const modalImage = overlay.querySelector('[data-orp-ctb-variant-modal-image]')
  const thumbsRoot = overlay.querySelector('[data-orp-ctb-variant-modal-thumbs-root]')
  const thumbsEl = overlay.querySelector('[data-orp-ctb-variant-modal-thumbs]')
  const wrapper = overlay.querySelector('[data-orp-ctb-variant-modal-thumbs-wrapper]')
  const prevEl = overlay.querySelector('[data-orp-ctb-modal-thumbs-prev]')
  const nextEl = overlay.querySelector('[data-orp-ctb-modal-thumbs-next]')
  if (!thumbsRoot || !wrapper) return

  destroyModalThumbsSwiper(overlay)
  wrapper.innerHTML = ''

  const images = Array.isArray(productData?.images) ? productData.images.filter((img) => img?.featured) : []

  if (images.length <= 1) {
    thumbsRoot.hidden = true
    const only = images[0]
    setModalFeaturedImage(
      modalImage,
      thumbsRoot,
      only?.featured || fallbackSrc || '',
      only?.alt || alt || '',
      { slideToActive: false },
    )
    return
  }

  thumbsRoot.hidden = false
  images.forEach((img, index) => {
    const slide = document.createElement('div')
    slide.className = 'swiper-slide orp-ctb__variant-modal__thumb-slide'

    const btn = document.createElement('button')
    btn.type = 'button'
    btn.className = 'orp-ctb__variant-modal__thumb'
    btn.setAttribute('aria-label', `View image ${index + 1}`)
    btn.dataset.orpCtbModalThumb = ''
    btn.dataset.featuredUrl = img.featured
    btn.dataset.thumbUrl = img.thumb || img.featured

    const thumbImg = document.createElement('img')
    thumbImg.src = img.thumb || img.featured
    thumbImg.alt = img.alt || alt || ''
    thumbImg.width = 120
    thumbImg.height = 120
    thumbImg.loading = 'lazy'
    btn.appendChild(thumbImg)

    btn.addEventListener('click', () => {
      setModalFeaturedImage(modalImage, thumbsRoot, img.featured, img.alt || alt || '')
    })

    slide.appendChild(btn)
    wrapper.appendChild(slide)
  })

  const startSrc = fallbackSrc || images[0].featured
  setModalFeaturedImage(modalImage, thumbsRoot, startSrc, alt || images[0].alt || '', {
    slideToActive: false,
  })

  requestAnimationFrame(() => {
    initModalThumbsSwiper(overlay, thumbsEl, prevEl, nextEl)
    const activeBtn = thumbsRoot.querySelector('[data-orp-ctb-modal-thumb].is-active')
    if (activeBtn && overlay._orpCtbThumbsSwiper) {
      const slides = Array.from(wrapper.querySelectorAll('.swiper-slide'))
      const idx = slides.findIndex((slide) => slide.contains(activeBtn))
      if (idx >= 0) {
        try {
          overlay._orpCtbThumbsSwiper.slideTo(idx, 0)
        } catch {
          /* ignore */
        }
      }
    }
  })
}

function getModalQty(overlay) {
  const inp = overlay.querySelector('[data-orp-ctb-modal-qty-input]')
  if (!inp) return 1
  const n = parseInt(inp.value, 10)
  return Number.isFinite(n) && n >= 1 ? Math.min(99, n) : 1
}

function setModalQty(overlay, qty) {
  const inp = overlay.querySelector('[data-orp-ctb-modal-qty-input]')
  if (!inp) return
  inp.value = String(Math.max(1, Math.min(99, qty)))
}

function updateModalCtaState(overlay) {
  const card = overlay._orpCtbCard
  const modalSelect = overlay.querySelector('[data-orp-ctb-variant-modal-select]')
  const modalAddBtn = overlay.querySelector('[data-orp-ctb-variant-modal-add]')
  const modalAddNewBtn = overlay.querySelector('[data-orp-ctb-variant-modal-add-new]')
  const actions = overlay.querySelector('[data-orp-ctb-variant-modal-actions]')
  if (!card || !modalSelect || !modalAddBtn) return

  const option = modalSelect.options[modalSelect.selectedIndex]
  const available = option && option.dataset.available !== 'false'
  const variantId = option?.value
  const lines = getSelectionLines(card)
  const alreadyIn = Boolean(variantId && lines.some((line) => String(line.variantId) === String(variantId)))

  if (modalAddNewBtn) modalAddNewBtn.hidden = true
  actions?.classList.remove('orp-ctb__variant-modal__actions--dual')

  if (!available) {
    modalAddBtn.disabled = true
    modalAddBtn.textContent = 'Out of stock'
    return
  }

  modalAddBtn.disabled = false

  if (alreadyIn) {
    modalAddBtn.textContent = 'Update'
    setModalQty(overlay, getSelections(card)[String(variantId)]?.qty || 1)
    return
  }

  modalAddBtn.textContent = 'Add to Build'
  if (lines.length > 0 && modalAddNewBtn) {
    modalAddNewBtn.hidden = false
    modalAddNewBtn.disabled = false
    actions?.classList.add('orp-ctb__variant-modal__actions--dual')
  }
}

function renderModalSelectionsList(overlay, container, config, sym) {
  const card = overlay._orpCtbCard
  const wrap = overlay.querySelector('[data-orp-ctb-variant-modal-selections]')
  const list = overlay.querySelector('[data-orp-ctb-variant-modal-selections-list]')
  if (!wrap || !list || !card) return

  const lines = getSelectionLines(card)
  list.innerHTML = ''

  if (!lines.length) {
    wrap.hidden = true
    return
  }

  wrap.hidden = false

  lines.forEach((line) => {
    const row = document.createElement('div')
    row.className = 'orp-ctb__variant-modal__selection-row'
    row.dataset.variantId = String(line.variantId)

    const title = document.createElement('span')
    title.className = 'orp-ctb__variant-modal__selection-title'
    title.textContent = line.title || `Variant ${line.variantId}`
    title.title = title.textContent

    const stepper = document.createElement('div')
    stepper.className = 'orp-ctb__qty-stepper orp-ctb__variant-modal__selection-qty'

    const dec = document.createElement('button')
    dec.type = 'button'
    dec.className = 'orp-ctb__qty-btn'
    dec.setAttribute('aria-label', 'Decrease quantity')
    dec.textContent = '−'

    const qtyInput = document.createElement('input')
    qtyInput.type = 'number'
    qtyInput.className = 'orp-ctb__qty-input'
    qtyInput.min = '1'
    qtyInput.max = '99'
    qtyInput.value = String(line.qty)
    qtyInput.setAttribute('aria-label', `Quantity for ${title.textContent}`)

    const inc = document.createElement('button')
    inc.type = 'button'
    inc.className = 'orp-ctb__qty-btn'
    inc.setAttribute('aria-label', 'Increase quantity')
    inc.textContent = '+'

    const removeBtn = document.createElement('button')
    removeBtn.type = 'button'
    removeBtn.className = 'orp-ctb__variant-modal__selection-remove'
    removeBtn.setAttribute('aria-label', `Remove ${title.textContent}`)
    removeBtn.innerHTML = '&times;'

    const applyQty = (nextQty) => {
      upsertSelectionWithSym(card, line.variantId, nextQty, sym, { replaceQty: true })
      refresh(container, config)
      renderModalSelectionsList(overlay, container, config, sym)
      updateModalCtaState(overlay)
    }

    dec.addEventListener('click', () => {
      const q = parseInt(qtyInput.value, 10) || 1
      if (q <= 1) {
        applyQty(0)
        return
      }
      applyQty(q - 1)
    })

    inc.addEventListener('click', () => {
      const q = parseInt(qtyInput.value, 10) || 1
      applyQty(Math.min(99, q + 1))
    })

    qtyInput.addEventListener('change', () => {
      let n = parseInt(qtyInput.value, 10)
      if (!Number.isFinite(n) || n < 1) n = 1
      if (n > 99) n = 99
      applyQty(n)
    })

    removeBtn.addEventListener('click', () => applyQty(0))

    stepper.appendChild(dec)
    stepper.appendChild(qtyInput)
    stepper.appendChild(inc)
    row.appendChild(title)
    row.appendChild(stepper)
    row.appendChild(removeBtn)
    list.appendChild(row)
  })
}

function updateModalFromOption(modalImage, modalPrice, modalCompare, option, sym, thumbsRoot) {
  if (!option) return
  if (option.dataset.imageUrl) {
    setModalFeaturedImage(modalImage, thumbsRoot, option.dataset.imageUrl, modalImage?.alt || '')
  }
  setPriceCompare(
    modalPrice,
    modalCompare,
    option.dataset.price,
    option.dataset.compareAtPrice,
    sym,
  )
}

function openVariantModal(overlay, card, sym, config, container) {
  const select = card.querySelector('[data-orp-ctb-select]')
  const btn = card.querySelector('[data-orp-ctb-select-options-btn]')
  const modalImage = overlay.querySelector('[data-orp-ctb-variant-modal-image]')
  const modalTitle = overlay.querySelector('[data-orp-ctb-variant-modal-title]')
  const modalPrice = overlay.querySelector('[data-orp-ctb-variant-modal-price]')
  const modalCompare = overlay.querySelector('[data-orp-ctb-variant-modal-compare]')
  const modalLabel = overlay.querySelector('[data-orp-ctb-variant-modal-label]')
  const modalSelect = overlay.querySelector('[data-orp-ctb-variant-modal-select]')
  const modalAddBtn = overlay.querySelector('[data-orp-ctb-variant-modal-add]')
  const thumbsRoot = overlay.querySelector('[data-orp-ctb-variant-modal-thumbs-root]')
  if (!select || !modalSelect || !modalAddBtn) return

  overlay._orpCtbCard = card
  overlay._orpCtbSelect = select

  const cardImg = card.querySelector('[data-orp-ctb-image]')
  const cardName = card.querySelector('.orp-ctb__card-name')
  const title = cardName?.textContent.trim() || ''
  if (modalTitle) modalTitle.textContent = title
  if (modalLabel) modalLabel.textContent = btn?.dataset.orpCtbOptionsLabel || 'Options'

  const productData = config?.products?.find(
    (p) => String(p.productId) === String(card.dataset.orpCtbProductId),
  )
  const selectedOption = select.options[select.selectedIndex]
  const fallbackSrc = selectedOption?.dataset.imageUrl || cardImg?.src || ''
  renderModalGallery(overlay, productData, fallbackSrc, title)

  modalSelect.innerHTML = select.innerHTML
  modalSelect.value = select.value
  setModalQty(overlay, 1)
  updateModalFromOption(
    modalImage,
    modalPrice,
    modalCompare,
    modalSelect.options[modalSelect.selectedIndex],
    sym,
    thumbsRoot,
  )
  renderModalSelectionsList(overlay, container, config, sym)
  updateModalCtaState(overlay)

  overlay.setAttribute('aria-hidden', 'false')
  requestAnimationFrame(() => {
    overlay.classList.add('active')
    document.body.style.overflow = 'hidden'
  })
}

function closeVariantModal(overlay) {
  overlay.classList.remove('active')
  overlay.setAttribute('aria-hidden', 'true')
  document.body.style.overflow = ''
  overlay._orpCtbCard = null
  overlay._orpCtbSelect = null
  destroyModalThumbsSwiper(overlay)
}

function applyModalUpsert(overlay, container, config, sym, { closeAfter, replaceQty }) {
  const card = overlay._orpCtbCard
  const select = overlay._orpCtbSelect
  const modalSelect = overlay.querySelector('[data-orp-ctb-variant-modal-select]')
  const modalAddBtn = overlay.querySelector('[data-orp-ctb-variant-modal-add]')
  if (!card || !select || !modalSelect || modalAddBtn?.disabled) return

  const option = modalSelect.options[modalSelect.selectedIndex]
  if (!option || option.dataset.available === 'false') return

  const qty = getModalQty(overlay)
  upsertSelectionWithSym(card, option.value, qty, sym, { replaceQty })

  select.value = modalSelect.value
  refresh(container, config)
  renderModalSelectionsList(overlay, container, config, sym)
  updateModalCtaState(overlay)

  if (closeAfter) {
    closeVariantModal(overlay)
  } else {
    setModalQty(overlay, 1)
  }
}

function initVariantModal(container, config, sym) {
  const overlay = container.querySelector('[data-orp-ctb-variant-modal-overlay]')
  if (!overlay) return

  const modalImage = overlay.querySelector('[data-orp-ctb-variant-modal-image]')
  const modalSelect = overlay.querySelector('[data-orp-ctb-variant-modal-select]')
  const modalPrice = overlay.querySelector('[data-orp-ctb-variant-modal-price]')
  const modalCompare = overlay.querySelector('[data-orp-ctb-variant-modal-compare]')
  const modalAddBtn = overlay.querySelector('[data-orp-ctb-variant-modal-add]')
  const modalAddNewBtn = overlay.querySelector('[data-orp-ctb-variant-modal-add-new]')
  const modalCloseBtn = overlay.querySelector('[data-orp-ctb-variant-modal-close]')
  const thumbsRoot = overlay.querySelector('[data-orp-ctb-variant-modal-thumbs-root]')
  const modalQtyInput = overlay.querySelector('[data-orp-ctb-modal-qty-input]')
  const modalQtyDec = overlay.querySelector('[data-orp-ctb-modal-qty-dec]')
  const modalQtyInc = overlay.querySelector('[data-orp-ctb-modal-qty-inc]')

  container.querySelectorAll('[data-orp-ctb-select-options-btn]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const card = btn.closest('.orp-ctb__card')
      if (card) openVariantModal(overlay, card, sym, config, container)
    })
  })

  modalSelect?.addEventListener('change', () => {
    updateModalFromOption(
      modalImage,
      modalPrice,
      modalCompare,
      modalSelect.options[modalSelect.selectedIndex],
      sym,
      thumbsRoot,
    )
    const card = overlay._orpCtbCard
    const variantId = modalSelect.value
    const alreadyIn = Boolean(
      card &&
        getSelectionLines(card).some((line) => String(line.variantId) === String(variantId)),
    )
    if (!alreadyIn) setModalQty(overlay, 1)
    updateModalCtaState(overlay)
  })

  modalQtyDec?.addEventListener('click', () => {
    setModalQty(overlay, getModalQty(overlay) - 1)
  })
  modalQtyInc?.addEventListener('click', () => {
    setModalQty(overlay, getModalQty(overlay) + 1)
  })
  modalQtyInput?.addEventListener('change', () => {
    setModalQty(overlay, getModalQty(overlay))
  })

  modalAddBtn?.addEventListener('click', () => {
    const card = overlay._orpCtbCard
    if (!card || !modalSelect) return
    const variantId = modalSelect.value
    const alreadyIn = getSelectionLines(card).some((line) => String(line.variantId) === String(variantId))
    applyModalUpsert(overlay, container, config, sym, {
      closeAfter: true,
      replaceQty: alreadyIn,
    })
  })

  modalAddNewBtn?.addEventListener('click', () => {
    applyModalUpsert(overlay, container, config, sym, {
      closeAfter: false,
      replaceQty: false,
    })
  })

  modalCloseBtn?.addEventListener('click', () => closeVariantModal(overlay))
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) closeVariantModal(overlay)
  })
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && overlay.classList.contains('active')) closeVariantModal(overlay)
  })
}

function clampQty(input) {
  let n = parseInt(input.value, 10)
  if (!Number.isFinite(n) || n < 0) n = 0
  if (n > 99) n = 99
  input.value = String(n)
}

function refresh(container, config) {
  updateBundleTotal(container, config)
  updateFooterThumbs(container, config)
  updateAddAllButton(container, config)
}

function initFooterVisibility(container) {
  const footer = container.querySelector('[data-orp-ctb-footer]')
  if (!footer || typeof IntersectionObserver === 'undefined') return

  const observer = new IntersectionObserver(
    ([entry]) => {
      footer.classList.toggle('orp-ctb__footer--visible', entry.isIntersecting)
    },
    { threshold: 0 },
  )
  observer.observe(container)
}

function openModalForCard(container, card, config, sym) {
  const overlay = container.querySelector('[data-orp-ctb-variant-modal-overlay]')
  if (!overlay || !card) return
  openVariantModal(overlay, card, sym, config, container)
}

function init(container) {
  const config = getConfig(container)
  if (!config) return
  const sym = config.currencySymbol || '$'

  container.querySelectorAll('[data-orp-ctb-select]').forEach((select) => {
    select.addEventListener('change', () => {
      const card = select.closest('.orp-ctb__card')
      const option = select.options[select.selectedIndex]
      if (!card || !option) return
      updateCardFromSelect(card, option, sym)
      refresh(container, config)
    })
  })

  initVariantModal(container, config, sym)

  container.querySelectorAll('.orp-ctb__card').forEach((card) => {
    const input = card.querySelector('[data-orp-ctb-qty-input]')
    const dec = card.querySelector('[data-orp-ctb-qty-dec]')
    const inc = card.querySelector('[data-orp-ctb-qty-inc]')
    if (!input) return

    if (cardHasVariants(card)) {
      seedSelectionsFromCard(card, sym)

      input.addEventListener('change', () => {
        clampQty(input)
        const lines = getSelectionLines(card)
        if (lines.length > 1) {
          setInputQty(card, getSelectionsTotalQty(card))
          openModalForCard(container, card, config, sym)
          return
        }
        if (lines.length === 1) {
          adjustSingleLineQty(card, getInputQty(card), sym)
        } else if (getInputQty(card) > 0) {
          openModalForCard(container, card, config, sym)
          setInputQty(card, 0)
          syncQtyState(card)
        }
        refresh(container, config)
      })

      dec?.addEventListener('click', () => {
        const lines = getSelectionLines(card)
        if (lines.length > 1) {
          openModalForCard(container, card, config, sym)
          return
        }
        if (lines.length === 1) {
          const q = lines[0].qty
          if (q <= 0) return
          adjustSingleLineQty(card, q - 1, sym)
          refresh(container, config)
          return
        }
      })

      inc?.addEventListener('click', () => {
        const lines = getSelectionLines(card)
        if (lines.length > 1) {
          openModalForCard(container, card, config, sym)
          return
        }
        if (lines.length === 1) {
          adjustSingleLineQty(card, Math.min(99, lines[0].qty + 1), sym)
          refresh(container, config)
          return
        }
        openModalForCard(container, card, config, sym)
      })

      return
    }

    input.addEventListener('change', () => {
      clampQty(input)
      syncQtyState(card)
      refresh(container, config)
    })
    input.addEventListener('input', () => {
      syncQtyState(card)
      refresh(container, config)
    })

    dec?.addEventListener('click', () => {
      const q = getCardQty(card)
      if (q <= 0) return
      input.value = String(q - 1)
      syncQtyState(card)
      refresh(container, config)
    })

    inc?.addEventListener('click', () => {
      input.value = String(Math.min(99, getCardQty(card) + 1))
      syncQtyState(card)
      refresh(container, config)
    })
  })

  const addAllBtn = container.querySelector('[data-orp-ctb-add-all]')
  if (addAllBtn) {
    addAllBtn.addEventListener('click', async () => {
      const items = buildAddAllItems(container, config)
      if (!items.length) return

      addAllBtn.disabled = true
      const originalText = addAllBtn.textContent
      addAllBtn.textContent = 'Adding…'

      try {
        await addToCart(items, true)
        addAllBtn.textContent = 'Added!'
        window.setTimeout(() => {
          addAllBtn.textContent = originalText
          refresh(container, config)
        }, 2000)
      } catch (err) {
        addAllBtn.textContent = originalText
        refresh(container, config)
        if (typeof alert !== 'undefined') alert(err?.message || 'Could not add items to cart.')
      }
    })
  }

  container.querySelectorAll('.orp-ctb__card').forEach((card) => {
    syncQtyState(card)
  })

  syncCurrentProductCard(container, config, sym)
  refresh(container, config)
  initFooterVisibility(container)

  if (container.querySelector('[data-orp-ctb-swiper]')) {
    initCarousel(container)
  }

  document.addEventListener('nova:product:render-form', () => {
    window.setTimeout(() => {
      syncCurrentProductCard(container, config, sym)
      refresh(container, config)
    }, 0)
  })
}

function run() {
  document.querySelectorAll('.orp-ctb[data-orp-ctb-section-id]').forEach((el) => init(el))
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', run)
} else {
  run()
}
